import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'

import {RELATES_TO_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsRelatesToListView'
import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  AddRelatesToInput,
  addRelatesToMutation,
  addRelatesToMutation$data,
} from './__generated__/addRelatesToMutation.graphql'
import type {OptimisticDependencyIssue} from './add-blocked-by-mutation'

function optimisticAddRelatesTo(
  store: RecordSourceProxy,
  input: AddRelatesToInput,
  optimisticIssue: OptimisticDependencyIssue,
) {
  // Only the viewer's issue is reliably in the store; the server response reconciles the symmetric reverse edge.
  const issueRecord = store.get(input.issueId)
  if (!issueRecord) return

  let relatedRecord = store.get(optimisticIssue.id)
  if (!relatedRecord) {
    relatedRecord = store.create(optimisticIssue.id, 'Issue')
  }

  relatedRecord.setValue(optimisticIssue.title, 'title')
  // Escape the plaintext picker title; the server response overwrites with real HTML.
  relatedRecord.setValue(escapeOptimisticTitleHTML(optimisticIssue.title), 'titleHTML')
  relatedRecord.setValue(optimisticIssue.url, 'url')
  relatedRecord.setValue(optimisticIssue.number, 'number')
  relatedRecord.setValue(optimisticIssue.state, 'state')
  relatedRecord.setValue(optimisticIssue.stateReason ?? null, 'stateReason')

  let repoRecord = store.get(optimisticIssue.repository.id)
  if (!repoRecord) {
    repoRecord = store.create(optimisticIssue.repository.id, 'Repository')
  }
  repoRecord.setValue(optimisticIssue.repository.nameWithOwner, 'nameWithOwner')
  const [owner, name] = optimisticIssue.repository.nameWithOwner.split('/')
  repoRecord.setValue(name, 'name')

  let ownerRecord = repoRecord.getLinkedRecord('owner')
  if (!ownerRecord) {
    ownerRecord = store.create(`${optimisticIssue.repository.id}:owner:optimistic`, 'Organization')
    repoRecord.setLinkedRecord(ownerRecord, 'owner')
  }
  ownerRecord.setValue(owner, 'login')

  relatedRecord.setLinkedRecord(repoRecord, 'repository')

  const connection = issueRecord.getLinkedRecord('relatesTo', {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    if (!existingNodes.some(node => node?.getDataID() === optimisticIssue.id)) {
      connection.setLinkedRecords([...existingNodes, relatedRecord], 'nodes')
    }
  }
}

export function addRelatesToMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimisticRelatedIssue,
}: {
  environment: Environment
  input: AddRelatesToInput
  onError?: (error: Error) => void
  onCompleted?: (response: addRelatesToMutation$data) => void
  optimisticRelatedIssue?: OptimisticDependencyIssue
}) {
  return commitMutation<addRelatesToMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation addRelatesToMutation($input: AddRelatesToInput!) @raw_response_type {
        addRelatesTo(input: $input) {
          issue {
            id
            topRelatesTo: relatesTo(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
          }
          relatedIssue {
            id
            topRelatesTo: relatesTo(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: optimisticRelatedIssue
      ? store => optimisticAddRelatesTo(store, input, optimisticRelatedIssue)
      : undefined,
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
    updater: store => {
      // Invalidate the picker's id set and the paginated "view all" connection on both symmetric endpoints.
      store.get(input.issueId)?.getLinkedRecord('relatesTo', {first: 100})?.invalidateRecord()
      store.get(input.relatedIssueId)?.getLinkedRecord('relatesTo', {first: 100})?.invalidateRecord()
      store
        .get(ConnectionHandler.getConnectionID(input.issueId, RELATES_TO_LIST_ALL_RELAY_CONNECTION))
        ?.invalidateRecord()
      store
        .get(ConnectionHandler.getConnectionID(input.relatedIssueId, RELATES_TO_LIST_ALL_RELAY_CONNECTION))
        ?.invalidateRecord()
    },
  })
}
